import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { Star } from 'lucide-react';

interface RatingJourneyProps {
  ratingJourney: Array<{ month: string; averageRating: number; bookCount: number }>;
}

export function RatingJourneyChart({ ratingJourney }: RatingJourneyProps) {
  if (ratingJourney.length === 0) {
    return null;
  }

  return (
    <Card className="card-elevated">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Star className="w-5 h-5 text-primary" />
          Your Rating Journey
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          How your average monthly ratings changed throughout the year
        </p>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={ratingJourney} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis 
              dataKey="month" 
              className="text-muted-foreground"
            />
            <YAxis 
              domain={[0, 5]} 
              ticks={[0, 1, 2, 3, 4, 5]}
              className="text-muted-foreground"
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px',
              }}
              labelStyle={{ color: 'hsl(var(--foreground))' }}
              formatter={(value: number, name: string, props: any) => [
                `${value.toFixed(2)} stars (${props.payload.bookCount} ${props.payload.bookCount === 1 ? 'book' : 'books'})`,
                'Average Rating'
              ]}
            />
            <ReferenceLine y={3} stroke="hsl(var(--muted-foreground))" strokeDasharray="3 3" />
            <Line 
              type="monotone" 
              dataKey="averageRating" 
              stroke="hsl(var(--primary))" 
              strokeWidth={3}
              dot={{ fill: 'hsl(var(--primary))', strokeWidth: 2, r: 5 }}
              activeDot={{ r: 7 }}
            />
          </LineChart>
        </ResponsiveContainer>
        
        <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-4">
          {ratingJourney.map((item) => (
            <div key={item.month} className="text-center p-3 rounded-lg bg-muted/50">
              <div className="font-semibold text-sm">{item.month}</div>
              <div className="flex items-center justify-center gap-1 mt-1">
                <Star className="w-4 h-4 fill-primary text-primary" />
                <span className="font-bold text-lg">{item.averageRating.toFixed(1)}</span>
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {item.bookCount} {item.bookCount === 1 ? 'book' : 'books'}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
